import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, MessageCircle, Trash2 } from 'react-feather';
import { collection, query, orderBy, limit, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';
import GlassPanel from '../components/GlassPanel';

interface ThreadDoc {
  id: string;
  title?: string;
  lastMessageAt?: any;
  messageCount?: number;
  type?: string;
}

function formatThreadDate(ts: any): string {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (sameDay) {
    return `Today · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (isYesterday) return 'Yesterday';
  return d.toLocaleDateString();
}

export default function ChatHistory() {
  const [threads, setThreads] = useState<ThreadDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.uid;

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const ref = collection(FIRESTORE_DB, `users/${userId}/chatThreads`);
        const q = query(ref, orderBy('lastMessageAt', 'desc'), limit(100));
        const snap = await getDocs(q);
        if (cancelled) return;
        const list: ThreadDoc[] = [];
        snap.forEach((d) => list.push({ id: d.id, ...(d.data() as any) }));
        setThreads(list);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const filtered = threads.filter((t) =>
    (t.title || '').toLowerCase().includes(filter.toLowerCase())
  );

  const open = (threadId: string) => {
    navigate('/f8chat', { state: { threadId } });
  };

  const handleDelete = async (e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    if (!userId) return;
    if (!confirm('Delete this chat? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(FIRESTORE_DB, `users/${userId}/chatThreads/${threadId}`));
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
    } catch (err) {
      console.error('delete thread failed:', err);
    }
  };

  if (!userId) {
    return (
      <div className="page-shell">
        <div className="page-shell__inner" style={{ maxWidth: 760 }}>
          <SEO title="Chat history | pplkpr" description="Your f8 chat history" path="/chat-history" />
          <div style={{ padding: '4rem 2rem', textAlign: 'center', color: 'rgba(220,200,240,0.6)' }}>
            Sign in to see your chat history.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-shell__inner" style={{ maxWidth: 760 }}>
        <SEO title="Chat history | pplkpr" description="Your f8 chat history" path="/chat-history" />

        <div className="page-header" style={{ marginBottom: 18 }}>
          <div>
            <h1 className="page-header__title">chat history</h1>
            <div className="page-header__subtitle">your conversations with f8</div>
          </div>
          <button
            className="primary-button"
            onClick={() => navigate('/f8chat')}
          >
            new chat
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="search history..."
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 12,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: '#fff',
              fontSize: 13.5,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
        </div>

        {loading && (
          <div style={{ padding: '3rem 0', textAlign: 'center', color: 'rgba(220,200,240,0.5)' }}>
            loading…
          </div>
        )}

        {!loading && error && (
          <GlassPanel hover={false} style={{ padding: 18 }}>
            <div style={{ color: '#ff6b6b' }}>{error}</div>
          </GlassPanel>
        )}

        {!loading && !error && filtered.length === 0 && (
          <GlassPanel hover={false} style={{ padding: '3rem 2rem', textAlign: 'center' }}>
            <div style={{ color: 'rgba(220,200,240,0.7)', marginBottom: 12 }}>
              {threads.length === 0 ? 'no chat history yet' : 'no matches'}
            </div>
            {threads.length === 0 && (
              <button className="primary-button" onClick={() => navigate('/f8chat')}>
                start a chat
              </button>
            )}
          </GlassPanel>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((t) => {
              const isVoice = t.type === 'voice' || /voice/i.test(t.title || '');
              return (
                <GlassPanel
                  key={t.id}
                  onClick={() => open(t.id)}
                  style={{ padding: 14 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isVoice
                        ? 'linear-gradient(135deg, rgba(217,70,239,0.25), rgba(124,58,237,0.18))'
                        : 'rgba(255,255,255,0.05)',
                      flexShrink: 0,
                      color: isVoice ? '#f0abfc' : 'rgba(220,200,240,0.7)',
                    }}>
                      {isVoice ? <Mic size={16} /> : <MessageCircle size={16} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 14, fontWeight: 600, color: '#fff',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {t.title || 'Untitled chat'}
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(220,200,240,0.5)', marginTop: 2 }}>
                        {formatThreadDate(t.lastMessageAt)}
                        {typeof t.messageCount === 'number' ? ` · ${t.messageCount} msg` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, t.id)}
                      aria-label="Delete chat"
                      style={{
                        background: 'none', border: 0,
                        color: 'rgba(220,200,240,0.4)',
                        cursor: 'pointer', padding: 6, borderRadius: 8,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#ff6b6b'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(220,200,240,0.4)'; }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </GlassPanel>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
